import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DailyCutoffDataType } from "../../../types";
import { formatDate } from "../../../utils/date";
import { formatNumberWithCommas } from "../../../utils/numbers";

type GenerateProps = {
  meterSerialNo: string;
  results: DailyCutoffDataType[];
  showLiters: boolean;
};

function formatVolume(value: number | null | undefined, showLiters: boolean): string {
  if (value === null || value === undefined) return "--";
  if (showLiters) return formatNumberWithCommas(Math.round(value));
  const truncated = Math.trunc(value / 10) / 100;
  return formatNumberWithCommas(truncated);
}

export default function generateDailyCutoffsPdf({ meterSerialNo, results, showLiters }: GenerateProps) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Top-right header: title
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Daily Consumption Report", pageWidth - 14, 12, { align: "right" });

  // Top-right header: "Meter No.: " in black, serial number in red
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const labelText = "Meter No.: ";
  const labelWidth = doc.getTextWidth(labelText);
  const fullLineWidth = doc.getTextWidth(`Meter No.: ${meterSerialNo}`);
  const lineStartX = pageWidth - 14 - fullLineWidth;
  doc.setTextColor(0, 0, 0);
  doc.text(labelText, lineStartX, 20);
  doc.setTextColor(220, 0, 0);
  doc.text(meterSerialNo, lineStartX + labelWidth, 20);
  doc.setTextColor(0, 0, 0);

  const unitLabel = showLiters ? "(l)" : "(units)";

  // Pre-compute raw consumption values for colour coding
  const rawConsumptions = results.map((row) => {
    if (row.previousVolume === null || row.previousVolume === undefined) return null;
    return row.totalVolume - row.previousVolume;
  });

  const body = results.map((row, i) => {
    const consumption = rawConsumptions[i];
    return [
      formatDate(row.deviceDateTime),
      formatVolume(row.previousVolume, showLiters),
      formatVolume(row.totalVolume, showLiters),
      consumption !== null ? formatVolume(Math.abs(consumption), showLiters) : "--",
      row.temperature !== null && row.temperature !== undefined
        ? Number(row.temperature).toFixed(2)
        : "--",
      row.alarmSummary || "None",
    ];
  });

  autoTable(doc, {
    head: [[
      "Date",
      `Previous Rd. ${unitLabel}`,
      `Current Rd. ${unitLabel}`,
      `Consumption ${unitLabel}`,
      "Temp (°C)",
      "Alarm",
    ]],
    body,
    startY: 25,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 3) {
        const consumption = rawConsumptions[data.row.index];
        if (consumption !== null && consumption < 0) {
          data.cell.styles.textColor = [220, 0, 0] as any;
        }
      }
    },
  });

  doc.save(`daily-report-${meterSerialNo}.pdf`);
}
